import React, { useState, useCallback, useEffect, lazy, Suspense } from "react";
import { CheckCircle2, Circle, Plus, X, Clock, AlertCircle, ChevronDown, ChevronRight, Loader2, ExternalLink, Edit2, Trash2, Check, FileEdit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from '@/hooks/useAuth';
import { useCurrentOrganization } from '@/hooks/useCurrentOrganization';
import { NOTIFICATION_TYPES } from '@/types/notification';
import { toast } from "@/components/ui/sonner";
import { useNavigate } from "@tanstack/react-router";
import type { ConversationTodoItem, ConversationTodoList } from "../types/chat";
import { useCreateTodo, useCreateSubtask } from '@/hooks/useTodos';
import { useCreateNotification } from '@/hooks/useNotifications';
import type { Todo, CreateTodoRequest, UpdateTodoRequest } from '@/types/todo';
import VoiceInputButton from '@/components/voice-input/VoiceInputButton';
import { cn } from '@flowstate/utils';
import { logger } from "@/lib/logger";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Lazy load TodoForm for the "Edit as Task" flow
const TodoForm = lazy(() => import('@/components/admin/todos/TodoForm'));

interface ChatTodoListProps {
  todoList: ConversationTodoList;
  messageId: string;
  onConvert?: (taskIds: string[]) => void;
  onUpdate?: (items: ConversationTodoItem[]) => void;
}

const priorityColors: Record<string, string> = {
  low: 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  high: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

const priorityIcons: Record<string, React.ReactNode> = {
  low: <Clock className="h-3 w-3" />,
  medium: <AlertCircle className="h-3 w-3" />,
  high: <AlertCircle className="h-3 w-3" />,
};

export const ChatTodoList: React.FC<ChatTodoListProps> = ({ todoList, messageId: _messageId, onConvert, onUpdate }) => {
  const { user, session } = useAuth();
  const { currentOrganization } = useCurrentOrganization();
  const currentOrgId = currentOrganization?.id;
  const navigate = useNavigate();
  // Canonical write path: TanStack DB collection mutations.
  const todoAuth = {
    userId: user?.id ?? "",
    accessToken: session?.access_token ?? "",
    orgId: currentOrgId,
  };
  const createTodo = useCreateTodo(todoAuth);
  const createSubtask = useCreateSubtask(todoAuth);
  const createNotification = useCreateNotification(user?.id ?? "");
  
  const [items, setItems] = useState<ConversationTodoItem[]>(todoList.items);

  // Sync with parent when items change
  useEffect(() => {
    onUpdate?.(items);
  }, [items, onUpdate]);
  const [newItemText, setNewItemText] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [convertedTaskIds, setConvertedTaskIds] = useState<string[]>([]);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [editPriority, setEditPriority] = useState<string>("medium");
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  
  // "Edit as Task" flow — opens TodoForm pre-filled with action item data
  const [taskFormOpen, setTaskFormOpen] = useState(false);
  const [taskFormItem, setTaskFormItem] = useState<ConversationTodoItem | null>(null);
  const [taskFormLoading, setTaskFormLoading] = useState(false);

  const toggleItem = useCallback((itemId: string) => {
    setItems(prevItems => 
      prevItems.map(item => 
        item.id === itemId 
          ? { ...item, completed: !item.completed }
          : item
      )
    );

    // Here you would typically persist to backend
    logger.info("🔄 Updated todo item:", { itemId });
  }, []);

  const addItem = useCallback(() => {
    if (!newItemText.trim()) return;

    const newItem: ConversationTodoItem = {
      id: Date.now().toString(),
      text: newItemText.trim(),
      completed: false,
      priority: 'medium',
      createdAt: new Date().toISOString(),
    };

    setItems(prevItems => [...prevItems, newItem]);
    setNewItemText("");
    setShowAddForm(false);

    logger.info("➕ Added new todo item:", { newItem });
  }, [newItemText]);

  /**
   * Toggle subtask expansion
   */
  const toggleExpanded = useCallback((itemId: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  }, []);

  /**
   * Convert all todo items to real tasks in the database
   * Supports nested subtasks!
   */
  const convertToTasks = useCallback(async () => {
    if (!user) {
      toast.error("Please sign in to convert items to tasks");
      return;
    }

    const itemsToConvert = items.filter(item => !item.completed && !item.convertedToTaskId);
    if (itemsToConvert.length === 0) {
      toast.info("No items to convert (all completed or already converted)");
      return;
    }

    setIsConverting(true);
    const createdTaskIds: string[] = [];
    let totalSubtasksCreated = 0;
    const errors: string[] = [];

    try {
      // Process each item
      for (const item of itemsToConvert) {
        try {
          // Create the parent task with organization scoping — collection insert
          const created = await createTodo.mutateAsync({
            title: item.text,
            priority: item.priority ?? 'medium',
            due_date: item.dueDate,
            description: `Created from Cortex conversation: ${todoList.context}`,
            organization_id: item.organization_id ?? currentOrgId ?? undefined,
          });

          const parentTaskId = created.id;
          createdTaskIds.push(parentTaskId);
          let _subtaskCount = 0;

          // Create subtasks if any
          if (item.subtasks && item.subtasks.length > 0) {
            for (const subtask of item.subtasks) {
              if (!subtask.completed) {
                try {
                  await createSubtask.mutateAsync({
                    parent_id: parentTaskId,
                    title: subtask.text,
                    priority: subtask.priority ?? item.priority ?? 'medium',
                    due_date: subtask.dueDate ?? item.dueDate,
                  });
                  _subtaskCount++;
                  totalSubtasksCreated++;
                } catch (subtaskError) {
                  logger.error(`Error creating subtask "${subtask.text}":`, { error: subtaskError });
                  errors.push(`Subtask "${subtask.text}"`);
                }
              }
            }
          }

          // Mark as converted in local state
          setItems(prev => prev.map(i =>
            i.id === item.id ? { ...i, convertedToTaskId: parentTaskId } : i
          ));
        } catch (taskError) {
          logger.error(`Error creating task "${item.text}":`, { error: taskError });
          errors.push(`Task "${item.text}"`);
        }
      }

      setConvertedTaskIds(createdTaskIds);
      
      // Build success message with accurate counts
      let successMessage = '';
      if (createdTaskIds.length === 0) {
        toast.error("No tasks were created. Please check for errors.");
        return;
      } else if (totalSubtasksCreated > 0) {
        successMessage = `Created ${createdTaskIds.length} task(s) with ${totalSubtasksCreated} subtask(s)!`;
      } else {
        successMessage = `Created ${createdTaskIds.length} task(s)!`;
      }
      
      if (errors.length > 0) {
        successMessage += ` (${errors.length} item(s) failed)`;
      }

      // Persist in-app notification so it appears in the Notification Center —
      // collection insert into the caller's notifications shape.
      if (user?.id) {
        createNotification.mutateAsync({
          type: NOTIFICATION_TYPES.TASK_ASSIGNED,
          user_id: user.id,
          message_preview: `Action items converted to tasks — ${successMessage} (from Cortex action items)`,
          data: {
            title: 'Action items converted to tasks',
            message: `${successMessage} (from Cortex action items)`,
            organization_id: currentOrgId,
            created_task_ids: createdTaskIds,
            context: todoList.context,
          },
        }).catch((err: unknown) => {
          logger.warn('Failed to send task conversion notification:', { error: err });
        });
      }
      
      toast.success(successMessage, {
        duration: 8000,
        action: {
          label: "View Tasks",
          onClick: () => navigate({ to: "/tasks" }),
        },
      });

      // Callback for parent component
      onConvert?.(createdTaskIds);

    } catch (error) {
      logger.error("Error converting to tasks:", { error: error });
      toast.error("Failed to convert some items. Please try again.");
    } finally {
      setIsConverting(false);
    }
  }, [user, currentOrgId, createTodo, createSubtask, createNotification, items, todoList.context, navigate, onConvert]);

  const removeItem = useCallback((itemId: string) => {
    setItems(prevItems => prevItems.filter(item => item.id !== itemId));
    setDeleteConfirmId(null);
    toast.success("Action item removed");
    logger.info("🗑️ Removed todo item:", { itemId });
  }, []);

  const startEditItem = useCallback((item: ConversationTodoItem) => {
    setEditingItemId(item.id);
    setEditText(item.text);
    setEditPriority(item.priority ?? 'medium');
  }, []);

  const saveEditItem = useCallback(() => {
    if (!editingItemId || !editText.trim()) return;
    
    setItems(prevItems => 
      prevItems.map(item => 
        item.id === editingItemId 
          ? { ...item, text: editText.trim(), priority: editPriority as 'low' | 'medium' | 'high' }
          : item
      )
    );
    
    setEditingItemId(null);
    setEditText("");
    setEditPriority("medium");
    toast.success("Action item updated");
  }, [editingItemId, editText, editPriority]);

  const cancelEditItem = useCallback(() => {
    setEditingItemId(null);
    setEditText("");
    setEditPriority("medium");
  }, []);

  /**
   * "Edit as Task" — opens TodoForm pre-filled with the action item data.
   * Economy of movement: click → form opens with data → assign → save → done.
   */
  const openAsTask = useCallback((item: ConversationTodoItem) => {
    setTaskFormItem(item);
    setTaskFormOpen(true);
  }, []);

  /**
   * Handle TodoForm submission — creates a real task from the action item.
   * After save, marks the item as converted and closes the form.
   */
  const handleTaskFormSubmit = useCallback(async (data: CreateTodoRequest | UpdateTodoRequest) => {
    if (!taskFormItem) return undefined;

    setTaskFormLoading(true);
    try {
      const createData = data as CreateTodoRequest;
      const created = await createTodo.mutateAsync(createData);
      const taskId = created.id;

      // Create subtasks if the action item had any
      if (taskFormItem.subtasks && taskFormItem.subtasks.length > 0) {
        for (const subtask of taskFormItem.subtasks) {
          if (!subtask.completed) {
            try {
              await createSubtask.mutateAsync({
                parent_id: taskId,
                title: subtask.text,
                priority: subtask.priority ?? createData.priority ?? 'medium',
                due_date: subtask.dueDate ?? createData.due_date,
              });
            } catch (err) {
              logger.warn('Failed to create subtask:', { error: err });
            }
          }
        }
      }

      // Mark item as converted in local state
      setItems(prev => prev.map(i =>
        i.id === taskFormItem.id ? { ...i, convertedToTaskId: taskId } : i
      ));
      setConvertedTaskIds(prev => [...prev, taskId]);

      toast.success('Task created successfully', {
        action: {
          label: "View Task",
          onClick: () => navigate({ to: "/tasks" }),
        },
      });

      setTaskFormOpen(false);
      setTaskFormItem(null);
      return { id: taskId };
    } catch (error) {
      logger.error('Error creating task from action item:', { error: error });
      toast.error('Failed to create task');
    } finally {
      setTaskFormLoading(false);
    }
    return undefined;
  }, [createTodo, createSubtask, taskFormItem, navigate]);

  /**
   * Build a pseudo-Todo object from a ConversationTodoItem for pre-filling TodoForm.
   * TodoForm uses the `todo` prop to populate its fields in edit mode.
   */
  const buildPrefilledTodo = useCallback((item: ConversationTodoItem): Todo | undefined => {
    if (!item) return undefined;
    return {
      id: '', // Empty — this is a new task, not an existing one
      title: item.text,
      description: `Created from Cortex conversation: ${todoList.context}`,
      status: 'todo' as const,
      priority: (item.priority ?? 'medium') as 'low' | 'medium' | 'high',
      due_date: item.dueDate ?? null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: user?.id ?? '',
      parent_id: null,
      organization_id: item.organization_id ?? currentOrgId ?? null, // Use item's org if available, fallback to active org
      project_id: item.project_id ?? null, // Use item's project if available
      assigned_to: null,
      assigned_by: null,
      assigned_at: null,
      category_id: null,
      client_id: null,
      is_asap: false,
    } as Todo;
  }, [todoList.context, user?.id, currentOrgId]);

  const completedCount = items.filter(item => item.completed).length;
  const totalCount = items.length;

  return (
    <div className="max-h-[600px] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <div>
          <h4 className="font-medium flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-primary" />
            {todoList.title}
          </h4>
          <p className="text-xs text-muted-foreground mt-1">
            {todoList.context} • {completedCount}/{totalCount} completed
          </p>
        </div>
        <Badge variant="outline" className="text-xs">
          Conversation Todo
        </Badge>
      </div>

      {/* Progress bar */}
      {totalCount > 0 && (
        <div className="mb-4 flex-shrink-0">
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(completedCount / totalCount) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Todo items - Scrollable container */}
      <div className="space-y-2 mb-3 overflow-y-auto flex-1 pr-2">
        {items.map((item) => {
          const isEditing = editingItemId === item.id;
          
          return (
            <div
              key={item.id}
              className={cn(
                "group flex items-start gap-3 p-3 rounded-md transition-all duration-200",
                item.completed 
                  ? 'bg-muted/50 opacity-75' 
                  : 'bg-background hover:bg-muted/30 border border-border/50'
              )}
            >
              {isEditing ? (
                // Edit mode
                <div className="flex-1 space-y-2">
                  <div className="flex items-start gap-2">
                    <Input
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      placeholder="Task description..."
                      className="text-sm flex-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          saveEditItem();
                        } else if (e.key === 'Escape') {
                          cancelEditItem();
                        }
                      }}
                      autoFocus
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={editPriority} onValueChange={setEditPriority}>
                      <SelectTrigger className="w-32 h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex gap-1 ml-auto">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        onClick={saveEditItem}
                        disabled={!editText.trim()}
                      >
                        <Check className="h-3 w-3 text-green-600" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        onClick={cancelEditItem}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                // Display mode
                <>
                  <button
                    onClick={() => toggleItem(item.id)}
                    className="flex-shrink-0 transition-colors duration-200 mt-1"
                    aria-label={item.completed ? "Mark as incomplete" : "Mark as complete"}
                  >
                    {item.completed ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : (
                      <Circle className="h-4 w-4 text-muted-foreground hover:text-primary" />
                    )}
                  </button>
                  
                  <div className="flex-1 min-w-0">
                    <span
                      className={cn(
                        "text-sm break-words",
                        item.completed ? "line-through text-muted-foreground" : "text-foreground"
                      )}
                    >
                      {item.text}
                    </span>
                    
                    {/* Priority badge */}
                    {item.priority && item.priority !== 'medium' && (
                      <Badge 
                        variant="secondary" 
                        className={cn("text-xs mt-1", priorityColors[item.priority])}
                      >
                        {priorityIcons[item.priority]}
                        <span className="ml-1 capitalize">{item.priority}</span>
                      </Badge>
                    )}
                    
                    {/* Subtasks */}
                    {item.subtasks && item.subtasks.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <button
                          onClick={() => toggleExpanded(item.id)}
                          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                        >
                          {expandedItems.has(item.id) ? (
                            <ChevronDown className="h-3 w-3" />
                          ) : (
                            <ChevronRight className="h-3 w-3" />
                          )}
                          {item.subtasks.length} subtask(s)
                        </button>
                        
                        {expandedItems.has(item.id) && (
                          <div className="ml-4 space-y-1">
                            {item.subtasks.map((subtask, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-xs">
                                <button
                                  onClick={() => {
                                    // Toggle subtask completion
                                    setItems(prev => prev.map(i => 
                                      i.id === item.id 
                                        ? {
                                            ...i,
                                            subtasks: i.subtasks?.map((s, sidx) => 
                                              sidx === idx ? { ...s, completed: !s.completed } : s
                                            )
                                          }
                                        : i
                                    ));
                                  }}
                                  className="flex-shrink-0"
                                >
                                  {subtask.completed ? (
                                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                                  ) : (
                                    <Circle className="h-3 w-3 text-muted-foreground" />
                                  )}
                                </button>
                                <span className={cn(
                                  subtask.completed ? "line-through text-muted-foreground" : "text-foreground"
                                )}>
                                  {subtask.text}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    {/* Edit as Task — opens TodoForm pre-filled */}
                    {!item.convertedToTaskId && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-1.5 text-xs text-primary hover:text-primary/80"
                        onClick={() => openAsTask(item)}
                        title="Open as task to assign & save"
                      >
                        <FileEdit className="h-3 w-3 mr-0.5" />
                        <span className="hidden sm:inline">Task</span>
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0"
                      onClick={() => startEditItem(item)}
                      title="Edit text & priority"
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                      onClick={() => setDeleteConfirmId(item.id)}
                      title="Delete action item"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </>
              )}
            </div>
          );
        })}
        
        {items.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Circle className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No action items yet</p>
          </div>
        )}
      </div>

      {/* Add item form */}
      <div className="flex-shrink-0">
        {showAddForm ? (
          <div className="flex flex-col gap-2 mb-3">
            <div className="flex gap-2">
              <Input
                value={newItemText}
                onChange={(e) => setNewItemText(e.target.value)}
                placeholder="Add a new action item..."
                className="text-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    addItem();
                  } else if (e.key === 'Escape') {
                    setShowAddForm(false);
                    setNewItemText("");
                  }
                }}
                autoFocus
              />
              <VoiceInputButton
                onTranscription={(text) => {
                  setNewItemText(prev => prev ? `${prev} ${text}` : text);
                }}
                size="sm"
                variant="ghost"
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={addItem} disabled={!newItemText.trim()}>
                Add
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setShowAddForm(false);
                  setNewItemText("");
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowAddForm(true)}
            className="w-full text-sm"
          >
            <Plus className="h-3 w-3 mr-1" />
            Add Action Item
          </Button>
        )}
      </div>

      {/* Footer actions */}
      <div className="flex-shrink-0">
        {user && totalCount > 0 && (
          <div className="mt-3 pt-3 border-t flex justify-between items-center text-xs text-muted-foreground">
            <span>
              {convertedTaskIds.length > 0 
                ? `✓ ${convertedTaskIds.length} task(s) created`
                : `${items.filter(i => !i.completed && !i.convertedToTaskId).length} item(s) ready to convert`
              }
            </span>
            <div className="flex gap-2">
              {convertedTaskIds.length > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-xs text-green-500 hover:text-green-600"
                  onClick={() => navigate({ to: "/tasks" })}
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  View Tasks
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                className="text-xs text-primary hover:text-primary/80"
                onClick={convertToTasks}
                disabled={isConverting || items.every(i => i.completed || i.convertedToTaskId)}
              >
                {isConverting ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    Converting...
                  </>
                ) : (
                  <>
                    <Plus className="h-3 w-3 mr-1" />
                    Convert to Tasks
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Account creation prompt for anonymous users */}
        {!user && (
          <div className="mt-3 pt-3 border-t">
            <div className="bg-primary/10 dark:bg-primary/20 border border-primary/20 dark:border-primary/30 rounded-md p-3">
              <p className="text-sm text-primary">
                💡 <strong>Create a free account</strong> to save this todo list and sync it across devices!
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteConfirmId !== null} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Action Item?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this action item from your list. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirmId && removeItem(deleteConfirmId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* "Edit as Task" dialog — TodoForm pre-filled with action item data */}
      {taskFormOpen && taskFormItem && (
        <Suspense fallback={
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        }>
          <TodoForm
            isOpen={taskFormOpen}
            onClose={() => {
              setTaskFormOpen(false);
              setTaskFormItem(null);
            }}
            onSubmit={handleTaskFormSubmit}
            todo={buildPrefilledTodo(taskFormItem)}
            isLoading={taskFormLoading}
          />
        </Suspense>
      )}
    </div>
  );
};
